import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { notificationApi, authApi, chatApi } from "../api"
import { useAuth } from "./AuthContext"
import { useSocket } from "./SocketContext"
import {
  notificationPermission,
  registerForPush,
  requestNotificationPermission,
  showDesktopNotification,
} from "../realtime/push"
import { armNotificationSound, playNotificationSound } from "../realtime/sound"

const NotificationContext = createContext(null)

/** Where a notification's ref_type + ref_id sends you. The service worker keeps
 *  its own copy of this; both are one edit if a route changes. */
export function routeForNotification(notification) {
  switch (notification?.ref_type) {
    case "task":
      return `/tasks/${notification.ref_id}`
    case "project":
      return `/projects/${notification.ref_id}`
    case "chat_room":
      return `/chat/${notification.ref_id}`
    case "organization":
      return `/clients/${notification.ref_id}`
    default:
      return "/"
  }
}

const TOAST_LIMIT = 3

/**
 * The bell, the in-app popup and the desktop notification.
 *
 * Three surfaces for one event, and each answers a different situation:
 *
 *   BELL      the durable record. Written server-side whatever the user's
 *             preferences, because a preference is "don't interrupt me", not
 *             "don't tell me". It is the only one that survives being offline.
 *   TOAST     an in-app popup, for someone who is looking at the app right now.
 *   DESKTOP   an OS notification, for someone who is not.
 *
 * The last two are mutually exclusive by design: `showDesktopNotification`
 * refuses while the tab is visible, so one event never produces both, which reads
 * as a duplicate rather than as thoroughness.
 */
export function NotificationProvider({ children }) {
  const { isAuthenticated, user } = useAuth()
  const { socket } = useSocket()
  const navigate = useNavigate()

  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [toasts, setToasts] = useState([])
  /* Unread conversations, not unread messages: the sidebar badge answers "how
     many threads are waiting for me", which is the number somebody acts on. A
     count of messages makes one chatty thread look like a morning's work. */
  const [chatUnread, setChatUnread] = useState(0)
  const [permission, setPermission] = useState(notificationPermission())
  const [loading, setLoading] = useState(false)

  // Held in a ref as well as state so the socket handler reads the current value
  // without re-subscribing on every notification that arrives.
  const pushTokenRef = useRef(null)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return
    setLoading(true)
    try {
      const data = await notificationApi.list({ limit: 30 })
      setNotifications(data.notifications || [])
      setUnreadCount(data.unread_count ?? 0)
    } catch {
      // The bell failing to load must not take the page with it.
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) refresh()
    else {
      setNotifications([])
      setUnreadCount(0)
      setToasts([])
    }
  }, [isAuthenticated, refresh])

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.key !== id))
  }, [])

  const openNotification = useCallback(
    (notification) => {
      navigate(routeForNotification(notification))
      if (!notification.is_read && notification.id) {
        notificationApi.markRead([notification.id]).catch(() => {})
        setNotifications((current) => current.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)))
        setUnreadCount((c) => Math.max(0, c - 1))
      }
    },
    [navigate],
  )

  /*
   * A notification arriving live.
   *
   * The unread count comes with the event rather than being re-fetched: one
   * notification to forty people would otherwise become forty immediate requests
   * for the badge — a thundering herd caused by the thing meant to save a request.
   */
  // Before the first click, not at the first notification: a browser will not
  // let a page make a noise until somebody has interacted with it, and by the
  // time a message arrives the gesture that would have unlocked audio is long
  // past.
  useEffect(() => {
    armNotificationSound()
  }, [])

  /*
   * Read through a ref, not a dependency.
   *
   * Putting the preference in this effect's dependency list would tear down and
   * rebuild the socket subscription every time somebody flicked the switch in
   * settings — and a resubscribe drops whatever arrives in the gap.
   */
  const wantsSoundRef = useRef(true)
  wantsSoundRef.current = user ? Boolean(user.notify_sound) : true

  useEffect(() => {
    if (!socket) return undefined

    const onNotification = ({ notification, unread_count: unread }) => {
      if (!notification) return

      setNotifications((current) => {
        // The socket event and a concurrent refresh can both deliver the same
        // row. De-duplicating on content id keeps it out of the list twice.
        if (current.some((n) => n.content_id === notification.id)) return current
        return [{ ...notification, content_id: notification.id, id: null, is_read: false }, ...current].slice(0, 50)
      })

      if (typeof unread === "number") setUnreadCount(unread)
      else setUnreadCount((c) => c + 1)

      const key = `${notification.id}-${Date.now()}`
      // Bounded, and oldest-first out. An unbounded stack covers the screen
      // during a busy minute and blocks the app underneath.
      setToasts((current) => [...current, { ...notification, key }].slice(-TOAST_LIMIT))

      const wentToTheDesktop = showDesktopNotification({
        title: notification.title,
        body: notification.body,
        tag: `${notification.ref_type || "tallywise"}:${notification.ref_id || ""}`,
        onClick: () => openNotification({ ...notification, content_id: notification.id }),
      })

      /*
       * One sound per event, whoever makes it.
       *
       * A desktop notification comes with the operating system's own chime. If
       * we played ours as well, every message while the tab was in the
       * background would make two noises a tenth of a second apart, which reads
       * as a stutter rather than as two notifications. `showDesktopNotification`
       * already returns false whenever it did not fire — tab in focus, no
       * permission, no API — so it is exactly the right gate.
       */
      if (!wentToTheDesktop && wantsSoundRef.current) {
        playNotificationSound({ refType: notification.ref_type, priority: notification.priority })
      }
    }

    socket.on("notification", onNotification)
    return () => socket.off("notification", onNotification)
  }, [socket, openNotification])

  /* A message in a thread you are not looking at also raises the badge — the
     server sends a notification for it, so this only keeps the count honest when
     the inbox event arrives first. */
  useEffect(() => {
    if (!socket) return undefined
    const onInbox = ({ unread_count: unread }) => {
      if (typeof unread === "number") setUnreadCount((c) => Math.max(c, unread))
    }
    socket.on("message_inbox", onInbox)
    return () => socket.off("message_inbox", onInbox)
  }, [socket])

  /*
   * ── The chat badge ─────────────────────────────────────────────────────────
   *
   * Counted from the room list rather than kept as a running tally. A tally has
   * to be decremented when a thread is read, and reading happens in another
   * tab, on a phone, or from a desktop notification — all of which this tab
   * never hears about, so the number drifts upward and stops meaning anything.
   * Re-reading the list is one cheap request and is always right.
   */
  const refreshChatUnread = useCallback(async () => {
    if (!isAuthenticated) {
      setChatUnread(0)
      return
    }
    try {
      const data = await chatApi.rooms({ limit: 200 })
      setChatUnread((data.rooms || []).filter((room) => Number(room.unread_count) > 0).length)
    } catch {
      // Leave the last known figure. A failed request is not "nothing waiting".
    }
  }, [isAuthenticated])

  useEffect(() => {
    refreshChatUnread()
  }, [refreshChatUnread])

  /*
   * Coalesced, because a burst of messages is one question.
   *
   * Ten messages arriving in a thread would otherwise be ten identical requests
   * for a number that only changed on the first one.
   */
  useEffect(() => {
    if (!socket) return undefined
    let timer = null
    const bump = () => {
      clearTimeout(timer)
      timer = setTimeout(refreshChatUnread, 400)
    }
    socket.on("message_inbox", bump)
    return () => {
      clearTimeout(timer)
      socket.off("message_inbox", bump)
    }
  }, [socket, refreshChatUnread])

  /* A click on a background (service worker) notification, relayed by the worker. */
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return undefined
    const onMessage = (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK" && event.data.url) navigate(event.data.url)
    }
    navigator.serviceWorker.addEventListener("message", onMessage)
    return () => navigator.serviceWorker.removeEventListener("message", onMessage)
  }, [navigate])

  /**
   * Turn on desktop notifications.
   *
   * Must be called from a click. Browsers block — sometimes permanently — a
   * permission prompt with no user gesture behind it, and a denial is sticky
   * enough that the user has to dig into site settings to undo it.
   */
  const enableDesktopNotifications = useCallback(async () => {
    const result = await requestNotificationPermission()
    /*
     * "suppressed" is an outcome, not a permission state.
     *
     * The browser is still at "default" — nothing was granted and nothing was
     * refused — so storing it here would make every `permission === "default"`
     * branch in the app stop matching, and the button that offers to ask would
     * disappear for the one person who most needs it. It goes back to the
     * caller, which is what puts the explanation on screen.
     */
    setPermission(result === "suppressed" ? notificationPermission() : result)
    if (result !== "granted") return result

    // Registering for FCM is what makes a notification arrive with the tab
    // closed. When it is not configured, in-tab popups still work — so this
    // failing is not a failure of the feature.
    const token = await registerForPush()
    if (token) {
      pushTokenRef.current = token
      await authApi.registerDevice(token, "web").catch(() => {})
    }
    return result
  }, [])

  /* Unregister this device on sign-out, so the laptop stops buzzing for an
     account that is no longer signed in on it. */
  useEffect(() => {
    if (isAuthenticated || !pushTokenRef.current) return
    const token = pushTokenRef.current
    pushTokenRef.current = null
    authApi.unregisterDevice(token).catch(() => {})
  }, [isAuthenticated])

  /* Re-register on load for someone who already granted permission, since an FCM
     token can be rotated by the browser between visits. */
  useEffect(() => {
    if (!isAuthenticated || permission !== "granted" || pushTokenRef.current) return
    registerForPush().then((token) => {
      if (!token) return
      pushTokenRef.current = token
      authApi.registerDevice(token, "web").catch(() => {})
    })
  }, [isAuthenticated, permission, user?.id])

  /**
   * One of them, marked read without opening it.
   *
   * "Mark all read" is the only control there was, which makes the bell a
   * choice between reading everything and clearing everything — so the one
   * notification somebody has dealt with stays bold beside twelve they have
   * not. This is the single-row version, and it deliberately does NOT navigate:
   * acknowledging something and going to look at it are different acts.
   */
  const markOneRead = useCallback(async (notification) => {
    if (!notification?.id || notification.is_read) return
    setNotifications((current) => current.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)))
    setUnreadCount((c) => Math.max(0, c - 1))
    try {
      const data = await notificationApi.markRead([notification.id])
      if (data?.unread_count != null) setUnreadCount(data.unread_count)
    } catch {
      // The optimistic update stands; the next refresh corrects it if needed.
    }
  }, [])

  const markAllRead = useCallback(async () => {
    setNotifications((current) => current.map((n) => ({ ...n, is_read: true })))
    setUnreadCount(0)
    try {
      const data = await notificationApi.markRead()
      setUnreadCount(data.unread_count ?? 0)
    } catch {
      // The optimistic update stands; the next refresh corrects it if needed.
    }
  }, [])

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      toasts,
      chatUnread,
      refreshChatUnread,
      loading,
      permission,
      refresh,
      markAllRead,
      markOneRead,
      openNotification,
      dismissToast,
      enableDesktopNotifications,
    }),
    [
      notifications,
      unreadCount,
      toasts,
      chatUnread,
      refreshChatUnread,
      loading,
      permission,
      refresh,
      markAllRead,
      markOneRead,
      openNotification,
      dismissToast,
      enableDesktopNotifications,
    ],
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error("useNotifications must be used inside a NotificationProvider")
  return ctx
}
